import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhaseManager } from './PhaseManager';
import { AIWorker, Phase } from '../types';

// ═══════════════════════════════════════════════════════════
//  AssignHub — single "assign a task" surface for every agent
//
//  Each agent has its own task shape:
//    • claude-coder    — phase.md flow + freeform prompt
//    • claw-orchestrator — plan an uploaded phase (auto-distribute modules)
//    • claude-tester   — write tests for a module (planned: not wired yet)
//    • miro-architect  — Miro architecture build
//
//  UX: big agent picker on top, context-appropriate form below.
//  Only one form is mounted at a time — the picker is the router.
// ═══════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

type AgentId = 'claude-coder' | 'claude-coder-sonnet' | 'claw-orchestrator' | 'claude-tester' | 'miro-architect';

interface AgentCard {
  id: AgentId;
  name: string;
  role: string;
  mood: string;
  color: string;
  description: string;
  chips: string[];
  taskModes: string;
}

const AGENTS: AgentCard[] = [
  {
    id: 'claude-coder',
    name: 'Claude',
    role: 'Programmer',
    mood: 'Medium',
    color: 'var(--peach)',
    description: 'Пишет код по .md-фазе или freeform-заданию. Коммитит на ветку phase/… и пушит.',
    chips: ['phase.md', 'freeform prompt', 'repo access', 'self-check'],
    taskModes: 'Фаза (автоматически раскидает по модулям) или прямая задача в чат.',
  },
  {
    id: 'claude-coder-sonnet',
    name: 'Sonny',
    role: 'Programmer',
    mood: 'Sonnet 4.6',
    color: 'var(--sage)',
    description: 'То же, что Claude, но на Sonnet 4.6 — в 5× дешевле Medium. Для тестовых прогонов, лёгких фаз и когда не хочется жечь Medium.',
    chips: ['cheap', 'phase.md', 'freeform prompt', 'repo access'],
    taskModes: 'Тот же интерфейс, что и у Claude — но на дешёвой модели.',
  },
  {
    id: 'claw-orchestrator',
    name: 'Claw',
    role: 'Orchestrator',
    mood: 'Sonnet 4.6',
    color: 'var(--amber)',
    description: 'Разбирает .md-фазу, строит план, раздаёт модули команде и принимает работу.',
    chips: ['plan-phase', 'review', 'accept/reject', 'notify'],
    taskModes: 'Выбери фазу → Claw её распланирует и запустит цикл в команде.',
  },
  {
    id: 'claude-tester',
    name: 'Tess',
    role: 'Tester',
    mood: 'Haiku 4.5',
    color: 'var(--steel)',
    description: 'Пишет unit/e2e тесты, гоняет coverage, QA после каждой фазы.',
    chips: ['write-tests', 'coverage', 'regression'],
    taskModes: 'Пока не активирована — backend обновим в следующей фазе.',
  },
  {
    id: 'miro-architect',
    name: 'Mira',
    role: 'Architect',
    mood: 'Sonnet 4.6',
    color: 'var(--sage)',
    description: 'Строит архитектурную доску в Miro по описанию проекта или по фазе.',
    chips: ['miro board', 'pbr architecture', 'components', 'connectors'],
    taskModes: 'Опиши проект — Mira нарисует её архитектуру в твоём Miro.',
  },
];

// ─── Shared types / helpers ────────────────────────────

interface Props {
  workers: AIWorker[];
  phases: Phase[];
  onRefresh: () => void;
}

const postJSON = async (path: string, body: any) => {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

// ═══════════════════════════════════════════════════════════
//  Forms per agent
// ═══════════════════════════════════════════════════════════

// ─── Coder: phase flow + freeform prompt ─────────────────

type ProjectLite = { id: string; name: string; role?: string };
type CoderId = 'claude-coder' | 'claude-coder-sonnet' | 'claude-tester';

// Order matters — most-expensive-first reads as a "default → cheaper"
// scale. The cost label after each name makes it impossible to
// accidentally pick Opus on a tiny task: relative cost is right
// there in the dropdown.
const CODER_OPTIONS: Array<{ id: CoderId; label: string }> = [
  { id: 'claude-coder',         label: 'Claude · Programmer (Medium) — 1×' },
  { id: 'claude-coder-sonnet',  label: 'Sonny · Programmer (Sonnet 4.6) — 0.2× (в 5× дешевле)' },
  { id: 'claude-tester',        label: 'Tess · Tester (Haiku 4.5) — 0.07× (в 15× дешевле)' },
];

const ProjectPicker: React.FC<{
  projects: ProjectLite[];
  selected: string[];
  onToggle: (id: string) => void;
}> = ({ projects, selected, onToggle }) => {
  if (projects.length === 0) {
    return (
      <div className="form-empty" style={{ marginTop: 4 }}>
        Нет подключённых GitLab-репозиториев. Подключи проект во вкладке
        <b> GitLab</b> — после этого выбор появится здесь.
      </div>
    );
  }
  return (
    <div className="form-chips" style={{ marginTop: 4 }}>
      {projects.map((p) => {
        const active = selected.includes(p.id);
        return (
          <button
            key={p.id}
            type="button"
            className={`form-chip ${active ? 'form-chip-active' : ''}`}
            onClick={() => onToggle(p.id)}
            title={p.role ? `${p.name} · ${p.role}` : p.name}
          >
            {active ? '✓ ' : ''}{p.name}{p.role ? ` · ${p.role}` : ''}
          </button>
        );
      })}
    </div>
  );
};

const CoderForm: React.FC<Props> = ({ phases, onRefresh }) => {
  const [mode, setMode] = useState<'phase' | 'freeform'>('phase');
  const [coderId, setCoderId] = useState<CoderId>('claude-coder');
  const [prompt, setPrompt] = useState('');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectLite[]>([]);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  // ref-based dispatch latch (runPhase guard). useState would re-
  // render but not stop the second click from entering the handler.
  const dispatchLatch = useRef(false);

  // Last uploaded phase .md content — cached so we can fan it out across
  // selected repos without re-uploading. PhaseManager feeds it via the
  // onUploaded callback (it already reads file.text() during upload).
  const [lastPhase, setLastPhase] = useState<{ id: string; name: string; content: string } | null>(null);

  React.useEffect(() => {
    fetch(`${API}/api/gitlab/projects`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setProjects(data); })
      .catch(() => {});
  }, []);

  const toggleProject = (id: string) => {
    setProjectIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Dispatch N tasks, one per selected project. If nothing is selected,
  // fire a single project-less task (local sandbox) so the button is never
  // silently disabled without feedback.
  // phaseId is passed only by runPhase; sendFreeform leaves it
  // undefined so backend won't try to repaint a non-existent phase.
  const dispatchTasks = async (
    message: string,
    phaseId?: string,
  ): Promise<{ ok: number; fail: number; firstId?: string }> => {
    const targets = projectIds.length > 0 ? projectIds : [''];
    let ok = 0, fail = 0, firstId: string | undefined;
    for (const pid of targets) {
      const { ok: rOk, data } = await postJSON(`/api/workers/${coderId}/task`, {
        message,
        projectId: pid || undefined,
        phaseId,
      });
      if (rOk) { ok += 1; firstId = firstId ?? data.taskId; } else { fail += 1; }
    }
    return { ok, fail, firstId };
  };

  const sendFreeform = async () => {
    if (prompt.trim().length < 10) return;
    setSending(true);
    setLastResult(null);
    const { ok, fail, firstId } = await dispatchTasks(prompt);
    setSending(false);
    if (ok && !fail) {
      setLastResult(
        `✓ ${ok} задач${ok === 1 ? 'а' : ''} принят${ok === 1 ? 'а' : 'о'}${firstId ? ` (${firstId.substring(0, 8)})` : ''}`
      );
      setPrompt('');
    } else {
      setLastResult(`✗ Создано ${ok} из ${ok + fail}`);
    }
  };

  const runPhase = async () => {
    // Hard-guard against accidental double-click — see freeform path
    // for the same latch trick.
    if (!lastPhase || projectIds.length === 0) return;
    if (sending || dispatchLatch.current) return;
    dispatchLatch.current = true;
    setSending(true);
    setLastResult(null);
    try {
      // Single freeform task per repo — straightforward and guaranteed
      // to start (assuming Claude CLI + ANTHROPIC_API_KEY are wired).
      // Module-by-module orchestration via Claw is available in the
      // Orchestrator card ("Claw, планируй") for cases where you
      // want per-module progress in the Pipeline view.
      const message = `[Фаза: ${lastPhase.name}]\n\n${lastPhase.content}`;
      // Pass phaseId so backend can: (a) attribute spend to the
      // phase in /api/analytics/spend, (b) paint Pipeline modules
      // green on success.
      const { ok, fail, firstId } = await dispatchTasks(message, lastPhase.id);
      if (ok && !fail) {
        setLastResult(`✓ Запущено на ${ok} репо${firstId ? ` · first=${firstId.substring(0, 8)}` : ''}. Прогресс — у воркера Claude.`);
      } else {
        setLastResult(`✗ Запущено ${ok} из ${ok + fail}`);
      }
    } finally {
      setSending(false);
      setTimeout(() => { dispatchLatch.current = false; }, 800);
    }
  };

  const projectSelector = (
    <>
      <label className="form-label">Репозитории {projectIds.length > 0 && <span className="form-hint">— выбрано: {projectIds.length}</span>}</label>
      <ProjectPicker projects={projects} selected={projectIds} onToggle={toggleProject} />
    </>
  );

  const coderSelector = (
    <>
      <label className="form-label" style={{ marginTop: 14 }}>Исполнитель</label>
      <select
        className="form-select"
        value={coderId}
        onChange={(e) => setCoderId(e.target.value as CoderId)}
      >
        {CODER_OPTIONS.map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </select>
    </>
  );

  return (
    <div className="form-body">
      <div className="form-tabs">
        <button
          className={`form-tab ${mode === 'phase' ? 'form-tab-active' : ''}`}
          onClick={() => setMode('phase')}
        >
          Фаза из .md
        </button>
        <button
          className={`form-tab ${mode === 'freeform' ? 'form-tab-active' : ''}`}
          onClick={() => setMode('freeform')}
        >
          Прямое задание
        </button>
      </div>

      {mode === 'phase' && (
        <div className="form-section">
          {projectSelector}
          {coderSelector}

          <div style={{ marginTop: 18 }}>
            <PhaseManager
              onPhaseUploaded={onRefresh}
              onPhaseContent={(phase, content) => setLastPhase({ id: phase.id, name: phase.name, content })}
            />
          </div>

          {lastPhase && (
            <div className="form-actions" style={{ marginTop: 16 }}>
              {lastResult && (
                <span className={`form-result ${lastResult.startsWith('✓') ? 'ok' : 'bad'}`}>{lastResult}</span>
              )}
              <button
                className="btn-primary"
                onClick={runPhase}
                disabled={sending || projectIds.length === 0}
                title={projectIds.length === 0 ? 'Выбери хотя бы один репозиторий' : `Запустить "${lastPhase.name}" на ${projectIds.length} репо`}
              >
                {sending
                  ? 'Отправляю…'
                  : `Запустить "${lastPhase.name}" на ${projectIds.length} репо`}
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'freeform' && (
        <div className="form-section">
          {projectSelector}
          {coderSelector}

          <label className="form-label" style={{ marginTop: 14 }}>
            Что нужно сделать
          </label>
          <textarea
            className="form-textarea"
            rows={8}
            placeholder="Напр.: добавь endpoint POST /api/reports который отдаёт PDF по фазе. Все в стиле README репо."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />

          <div className="form-meta">
            <span>
              {prompt.length} символов · минимум 10
              {projectIds.length > 1 && ` · задача уйдёт на ${projectIds.length} репо`}
            </span>
          </div>

          <div className="form-actions">
            {lastResult && <span className={`form-result ${lastResult.startsWith('✓') ? 'ok' : 'bad'}`}>{lastResult}</span>}
            <button
              className="btn-primary"
              onClick={sendFreeform}
              disabled={sending || prompt.trim().length < 10}
            >
              {sending ? 'Отправляю…' : 'Отправить Claude'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Orchestrator: plan an uploaded phase ────────────────

const OrchestratorForm: React.FC<Props> = ({ phases, onRefresh }) => {
  const plannable = useMemo(
    () => phases.filter((p) => (p.status ?? 'uploaded') === 'uploaded' || (p.status ?? 'uploaded') === 'planning'),
    [phases]
  );
  const [phaseId, setPhaseId] = useState<string>('');
  const [autoAssign, setAutoAssign] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const selectedPhase = phases.find((p) => p.id === phaseId);

  const plan = async () => {
    if (!phaseId) return;
    setSending(true);
    setLastResult(null);
    const { ok, data } = await postJSON('/api/claw/plan-phase', { phaseId, autoAssign });
    setSending(false);
    setLastResult(ok
      ? `✓ Claw поставлен в очередь (${data.id?.substring(0, 8)}). Модули раскидаются автоматически.`
      : `✗ ${data.error || 'error'}`);
    if (ok) onRefresh();
  };

  return (
    <div className="form-body">
      <div className="form-section">
        <label className="form-label">Фаза для планирования</label>
        {plannable.length === 0 ? (
          <div className="form-empty">
            Нет фаз в статусе "uploaded". Сначала Claude загрузит .md файл во вкладке
            его карточки — потом Claw сможет её разобрать.
          </div>
        ) : (
          <>
            <select className="form-select" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
              <option value="">— Выбери фазу —</option>
              {plannable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.modules.length} модулей · {p.status || 'uploaded'}
                </option>
              ))}
            </select>

            {selectedPhase && (
              <div className="form-preview">
                <div className="form-preview-head">Что войдёт в план:</div>
                <ol className="form-preview-list">
                  {selectedPhase.modules.slice(0, 8).map((m) => (
                    <li key={m.id}>
                      <b>{m.name}</b>
                      {m.description && <span className="form-preview-desc"> — {m.description.substring(0, 120)}</span>}
                    </li>
                  ))}
                  {selectedPhase.modules.length > 8 && (
                    <li className="form-preview-more">+ ещё {selectedPhase.modules.length - 8} модулей</li>
                  )}
                </ol>
              </div>
            )}

            <label className="form-check">
              <input type="checkbox" checked={autoAssign} onChange={(e) => setAutoAssign(e.target.checked)} />
              <span>Сразу раскидать модули по воркерам (auto-assign)</span>
            </label>

            <div className="form-actions">
              {lastResult && <span className={`form-result ${lastResult.startsWith('✓') ? 'ok' : 'bad'}`}>{lastResult}</span>}
              <button className="btn-primary" onClick={plan} disabled={!phaseId || sending}>
                {sending ? 'Отправляю…' : 'Claw, планируй'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Tester (not wired yet) ──────────────────────────────

const TesterForm: React.FC<Props> = ({ phases }) => {
  return (
    <div className="form-body">
      <div className="form-section">
        <div className="form-empty">
          <b>Tess пока не активирована.</b> Backend-скиллы <code>write-tests</code>
          {' '}и<code> qa-phase</code> добавим в следующей фазе.
          Сейчас тесты пишет сам Claude в рамках self-check после каждой задачи.
          <br/><br/>
          Когда подключим — тут будет:
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Выбор фазы → модуля для которого писать тесты</li>
            <li>Test plan: что именно покрыть</li>
            <li>Выбор тест-раннера (jest / vitest / playwright / pytest)</li>
            <li>Coverage target</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─── Mira (Miro architect) ───────────────────────────────

const PROJECT_TYPES = ['webapp', 'api-service', 'data-pipeline', 'ml-system', 'platform', 'mobile-app', 'other'];

const ArchitectForm: React.FC<Props> = ({ phases }) => {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState<string>('webapp');
  const [tagsText, setTagsText] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [templateBoardId, setTemplateBoardId] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<{ ok: boolean; text: string; url?: string } | null>(null);

  const canSubmit = projectName.trim().length >= 3 && description.trim().length >= 20;

  const submit = async () => {
    if (!canSubmit) return;
    setSending(true);
    setLastResult(null);
    const payload = {
      projectName: projectName.trim(),
      projectDescription: description.trim(),
      projectType,
      tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
      phaseId: phaseId || undefined,
      templateBoardId: templateBoardId.trim() || undefined,
    };
    const { ok, data } = await postJSON('/api/miro/build-async', payload);
    setSending(false);
    if (ok) {
      setLastResult({
        ok: true,
        text: `✓ Задача в очереди (${data.taskId?.substring(0, 8)}). Mira строит доску — её URL появится в чате когда готово.`,
      });
    } else {
      setLastResult({ ok: false, text: `✗ ${data.error || 'error'}` });
    }
  };

  return (
    <div className="form-body">
      <div className="form-section">
        <label className="form-label">Название проекта <span className="form-req">*</span></label>
        <input
          type="text"
          className="form-input"
          placeholder="Напр.: Lato AI Dev Team"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
        />

        <label className="form-label" style={{ marginTop: 14 }}>
          Описание проекта <span className="form-req">*</span>
          <span className="form-hint"> — чем подробнее, тем точнее архитектура</span>
        </label>
        <textarea
          className="form-textarea"
          rows={6}
          placeholder="Опиши что делает проект, какие сервисы, какие данные хранятся, с какими внешними системами интегрируется…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <label className="form-label" style={{ marginTop: 14 }}>Тип проекта</label>
        <div className="form-chips">
          {PROJECT_TYPES.map((t) => (
            <button
              key={t}
              className={`form-chip ${projectType === t ? 'form-chip-active' : ''}`}
              onClick={() => setProjectType(t)}
              type="button"
            >
              {t}
            </button>
          ))}
        </div>

        <label className="form-label" style={{ marginTop: 14 }}>
          Теги (через запятую)
        </label>
        <input
          type="text"
          className="form-input"
          placeholder="saas, fintech, realtime, b2b"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
        />

        <label className="form-label" style={{ marginTop: 14 }}>
          Использовать фазу как контекст (опционально)
        </label>
        <select className="form-select" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
          <option value="">— Не использовать —</option>
          {phases.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <details className="form-advanced">
          <summary>Advanced: template Miro board</summary>
          <label className="form-label" style={{ marginTop: 10 }}>Board ID шаблона</label>
          <input
            type="text"
            className="form-input"
            placeholder="uXjVA..."
            value={templateBoardId}
            onChange={(e) => setTemplateBoardId(e.target.value)}
          />
        </details>

        <div className="form-actions">
          {lastResult && (
            <span className={`form-result ${lastResult.ok ? 'ok' : 'bad'}`}>
              {lastResult.text}
              {lastResult.url && <a href={lastResult.url} target="_blank" rel="noreferrer"> Открыть ↗</a>}
            </span>
          )}
          <button className="btn-primary" onClick={submit} disabled={!canSubmit || sending}>
            {sending ? 'Отправляю…' : 'Mira, рисуй архитектуру'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  AssignHub — the top-level component exported
// ═══════════════════════════════════════════════════════════

export const AssignHub: React.FC<Props> = ({ workers, phases, onRefresh }) => {
  const [agentId, setAgentId] = useState<AgentId>('claude-coder');
  const selected = AGENTS.find((a) => a.id === agentId)!;

  // Merge live workers data into agent cards so we can show
  // real status dot per agent without duplicating logic.
  const workerById = useMemo(() => {
    const map: Record<string, AIWorker> = {};
    for (const w of workers) map[w.id] = w;
    return map;
  }, [workers]);

  return (
    <div className="assign-hub">
      {/* Agent picker */}
      <div className="agent-grid">
        {AGENTS.map((a) => {
          const w = workerById[a.id];
          const isActive = agentId === a.id;
          return (
            <motion.button
              key={a.id}
              className={`agent-card ${isActive ? 'agent-active' : ''}`}
              onClick={() => setAgentId(a.id)}
              style={{ ['--agent-accent' as any]: a.color }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="agent-head">
                <div className="agent-name">{a.name}</div>
                <div className={`agent-dot agent-dot-${w?.status || 'idle'}`} />
              </div>
              <div className="agent-role">{a.role} · {a.mood}</div>
              <div className="agent-desc">{a.description}</div>
              <div className="agent-chips">
                {a.chips.map((c) => (
                  <span key={c} className="agent-chip">{c}</span>
                ))}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Context-sensitive form */}
      <div className="assign-form" style={{ ['--agent-accent' as any]: selected.color }}>
        <div className="assign-form-head">
          <div>
            <div className="assign-form-label">Задача для</div>
            <div className="assign-form-name">{selected.name} <span className="assign-form-role">— {selected.role}</span></div>
          </div>
          <div className="assign-form-modes">{selected.taskModes}</div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={agentId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22 }}
          >
            {agentId === 'claude-coder'      && <CoderForm       workers={workers} phases={phases} onRefresh={onRefresh} />}
            {agentId === 'claw-orchestrator' && <OrchestratorForm workers={workers} phases={phases} onRefresh={onRefresh} />}
            {agentId === 'claude-tester'     && <TesterForm      workers={workers} phases={phases} onRefresh={onRefresh} />}
            {agentId === 'miro-architect'    && <ArchitectForm   workers={workers} phases={phases} onRefresh={onRefresh} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
